import FormatCurrency from 'components/Common/FormatCurrency'
import LineDoorIcon from 'LuxKit/Icons/line/LineDoorIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import SwitchListItem from 'LuxKit/List/SwitchListItem'
import Sheet from 'components/utils/Sheet'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import React, { Fragment, useCallback, useContext } from 'react'
import { offerFlightsEnabled } from 'selectors/offerPage/offerPageSelectors'
import Divider from 'LuxKit/Divider'
import { rem } from 'polished'
import styled from 'styled-components'
import TextLink from 'LuxKit/TextLink'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { countOccupantsForFlights } from 'lib/offer/occupancyUtils'
import { FormattedMessage, useIntl } from 'react-intl'

const DividerContainer = styled.div`
  padding-inline: ${rem(16)};
`

interface Props {
  rooms: Array<OfferPageRoom>
  roomsConnected?: boolean
  flights?: boolean
  flightsEnabled?: boolean
  airport?: App.Airport
  totalFlightPrice?: number
  charterBundleRoute?: App.CharterBundleRoute
  bundledWithFlightsOnly?: boolean
}

function OfferPageTogglesSection({
  rooms,
  roomsConnected,
  flights,
  flightsEnabled,
  airport,
  totalFlightPrice,
  charterBundleRoute,
  bundledWithFlightsOnly,
}: Props) {
  const intl = useIntl()
  const hasMultipleRooms = useContext(OfferPageStateContext).rooms.length > 1

  const dispatch = useOfferPageDispatch()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const onConnectedRoomsChange = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleConnectedRooms })
  }, [dispatch])

  const onFlightsChange = useCallback(() => {
    dispatch({ type: OfferPageActions.setFlights, flights: !flights })
  }, [dispatch, flights])

  const changeAirport = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleFilter, filter: 'airport' })
    trackEvent('AIRPORT_FILTER_PRESSED')
  }, [dispatch, trackEvent])

  let flightPricePerPerson: number | undefined = undefined
  if (totalFlightPrice) {
    const flightOccupants = countOccupantsForFlights(rooms)
    const totalPeople = flightOccupants.adults + flightOccupants.children + flightOccupants.infants
    flightPricePerPerson = totalFlightPrice / totalPeople
  }

  const airportLink = airport ? <TextLink onClick={changeAirport}>{airport.code}</TextLink> : undefined
  let flightTitle: React.ReactNode
  if (bundledWithFlightsOnly && airport) {
    flightTitle = <FormattedMessage
      id="3R1Za4n1q9L04F3qUtnq"
      defaultMessage="Select flights from {airportLink}"
      description="Flight toggle title for bundled flights with airport selection link"
      values={{ airportLink }}
    />
  } else if (bundledWithFlightsOnly) {
    flightTitle = intl.formatMessage({
      id: 'iw2CuzTlPxgZJye-YlHI',
      defaultMessage: 'Select flights',
      description: 'Flight toggle title for bundled flights without airport',
    })
  } else if (airport) {
    flightTitle = <FormattedMessage
      id="M8UCMrkEWupx-4BT-twU"
      defaultMessage="Add flights from {airportLink}"
      description="Flight toggle title for optional flights with airport selection link"
      values={{ airportLink }}
    />
  } else {
    flightTitle = intl.formatMessage({
      id: 'J-_mdZUMktguQ2MkzuA2',
      defaultMessage: 'Add flights',
      description: 'Flight toggle title for optional flights without airport',
    })
  }

  let flightSubtitle: React.ReactNode | undefined
  if (flightPricePerPerson && !bundledWithFlightsOnly) {
    flightSubtitle = <FormattedMessage
      id="F1_n5kLX3ZK9V4Ay8F3E"
      defaultMessage="From {price}/adult"
      description="Flight price per adult subtitle in flight toggle on the form"
      values={{ price: <FormatCurrency value={flightPricePerPerson} format="rounded" /> }}
    />
  }

  const toggles = [
    {
      key: 'connectingRooms',
      visible: hasMultipleRooms && !charterBundleRoute,
      element: <SwitchListItem
        title={intl.formatMessage({
          id: 'yWFYFf0CqTZQhvrygklw',
          defaultMessage: 'Request connecting rooms',
          description: 'Toggle title to request connecting rooms in multi-room booking',
        })}
        subtitle={intl.formatMessage({
          id: 'cyz5YyVmTKvYvUWX0Zlb',
          defaultMessage: 'Subject to availability. Confirmed at check-in.',
          description: 'Subtitle explaining connecting rooms availability disclaimer',
        })}
        icon={<LineDoorIcon />}
        checked={roomsConnected}
        inputPlacement="end"
        onChange={onConnectedRoomsChange}
      />,
    },
    {
      key: 'flights',
      visible: flightsEnabled && !charterBundleRoute,
      element: <SwitchListItem
        title={flightTitle}
        subtitle={flightSubtitle}
        icon={<LinePlaneDepartureIcon />}
        checked={bundledWithFlightsOnly || flights}
        inputPlacement="end"
        onChange={bundledWithFlightsOnly ? undefined : onFlightsChange}
      />,
    },
  ].filter(toggle => toggle.visible)

  if (toggles.length === 0) {
    return null
  }

  return <Sheet background="neutral-eight">
    {toggles.map((toggle, i) => <Fragment key={toggle.key}>
      {i > 0 && <DividerContainer><Divider kind="primary" /></DividerContainer>}
      {toggle.element}
    </Fragment>)}
  </Sheet>
}

type MappedProps = Pick<Props,
  | 'rooms'
  | 'airport'
  | 'roomsConnected'
  | 'flights'
  | 'flightsEnabled'
  | 'charterBundleRoute'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  rooms: state.rooms,
  airport: state.airport,
  roomsConnected: state.isMultiRoomConnected,
  flights: state.flights,
  flightsEnabled: offerFlightsEnabled(state, appState),
  charterBundleRoute: state.charterBundleRoute,
}), OfferPageTogglesSection)
